/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { UFOv1, UFOv2 } from '@atlassian/post-office-frontend-performance-tracking';
import {
	InvalidMessageTemplateIdError,
	MessageRendererEmptyValidationResponseError,
} from '@post-office/errors';
import { MessageContextProvider, type MessageContextData } from '@post-office/message-context';
import { MaybeI18nProvider } from '@post-office/placement-contexts/i18n-provider';
import { MessageAnalyticsContextProvider } from '@post-office/placement-contexts/message-analytics-context';
import { FeatureFlaggedUfoWrapper } from '@post-office/placement-contexts/message-renderer-provider/ufo-wrapper';
import { TriggeredMessageValidator } from '@post-office/placement-contexts/message-renderer-provider/triggered-message-validator';
import { useWaitForTrigger } from '@post-office/placement-contexts/hooks/use-wait-for-trigger';
import { MaybeManualMessageViewedEvent } from '@post-office/placement-contexts/maybe-manual-message-viewed-event';
import type { TriggerType } from '@post-office/shared-contracts/types';
import { isClientTriggeredMessage } from '@post-office/placement-contexts/utils/is-client-triggered-message';
import { TrackMount } from '@post-office/placement-contexts/track-mount';
import {
	type ErrorBoundaryProps,
	withErrorBoundary,
} from '@post-office/placement-contexts/withErrorBoundary';
import { RecommendationProvider } from '@post-office/recommendation-context';
import type { MessageCreationType } from '@post-office/shared-contracts/message-template';
import {
	type ComponentProps,
	type FunctionComponent,
	type LazyExoticComponent,
	type MemoExoticComponent,
	type ReactNode,
	Suspense,
	type SuspenseProps,
	memo,
	useEffect,
	useMemo,
} from 'react';

import { ChoreographerWrapper } from './choreographer-wrapper';
import { type MessageCategory } from '../types';

type RenderableMessageProps<MessageTemplateId extends string = string> = {
	messageTemplateId: MessageTemplateId;
	messageInstanceId: string;
	messageCategory: MessageCategory;
	transactionAccountId?: string;
	recommendationSession?: {
		sessionId: string;
		entityid: string;
	};
	product?: string;
	loadingFallback?: React.ReactNode;
	postOffice?: {
		analyticsDetails?: MessageContextData['analyticsDetails'];
	};
	triggerId?: string;
	messageCreationType?: MessageCreationType;
	SuspenseBoundary?: typeof MaybeSuspenseBoundary;
	journeyId?: string;
	journeyTriggerId?: string;
	configSetId?: string;
};

type PlacementPropsShape = Record<string, unknown> | undefined;

type RenderableMessageComponent<T extends Record<string, unknown> = Record<string, unknown>> =
	| LazyExoticComponent<React.FC<T>>
	| React.FC<T>;

export type MessageProps<MessageComponents extends Record<string, RenderableMessageComponent>> = {
	[K in keyof MessageComponents]: ComponentProps<MessageComponents[K]> &
		RenderableMessageProps<Extract<K, string>>;
}[keyof MessageComponents];

export type MessageRendererType = MemoExoticComponent<
	(
		props: MessageProps<Record<string, RenderableMessageComponent>> &
			Record<string, unknown> &
			ErrorBoundaryProps,
	) => ReactNode
>;

export type InitializeMessageRenderer = () => MessageRendererType;

const STABLE_EMPTY_OBJECT = {};

/**
 * Wraps children in Suspense boundary when not in SSR environment.
 *
 * @param children
 * @param props
 * @constructor
 */
export const MaybeSuspenseBoundary: FunctionComponent<SuspenseProps> = ({ children, ...props }) => {
	return process.env.REACT_SSR ? <>{children}</> : <Suspense {...props}>{children}</Suspense>;
};

// Any is here as we never care about the value, and it stops the types getting more complex
export const asMessageRenderer = <
	MessageComponents extends Record<string, RenderableMessageComponent>,
>(
	messageComponents: MessageComponents,
	placementId: string,
	messageValidator?: (props: unknown) => MessageProps<MessageComponents>,
): {
	initializeMessageRenderer: InitializeMessageRenderer;
	MessageRenderer: MessageRendererType;
} => {
	const initializeMessageRenderer = <PlacementProps extends PlacementPropsShape>() => {
		// Return renderer component
		return memo(
			({
				messageTemplateId,
				messageInstanceId,
				recommendationSession,
				fallbackRender,
				loadingFallback,
				onError,
				messageCategory,
				postOffice,
				triggerId,
				messageCreationType,
				journeyId,
				journeyTriggerId,
				configSetId,
				SuspenseBoundary = Suspense,
				...data
			}: MessageProps<MessageComponents> & PlacementProps & ErrorBoundaryProps) => {
				const isUFOEnabled = FeatureGates.checkGate('enable_ufo_tracking_in_post_office');
				const isTriggerChoreographingEnabled = FeatureGates.checkGate(
					'post-office_enable_trigger-choreographing',
				);

				/// https://switcheroo.atlassian.com/ui/gates/e8049c84-32d5-4452-a52f-5b92940a1b55/key/post-office_enable-i18n-migration-pull
				const isI18nMigrationInAppEnabled = FeatureGates.checkGate(
					'post-office_enable-i18n-migration-pull',
				);

				const { markMessageFailed, markMessageUnmounted, messageSuccessComponent } = useMemo(() => {
					const messagePerformanceTracker = isUFOEnabled
						? new UFOv1.MessagePerformanceTracker(messageTemplateId)
						: undefined;

					const onMessageSuccess = () => {
						void messagePerformanceTracker?.markSuccess();
					};

					const isChunkLoadError = (error: Error): boolean => {
						return error?.name === 'ChunkLoadError';
					};

					const markMessageFailed =
						(onError?: (error: Error, info: React.ErrorInfo) => void) =>
						(error: Error, info: React.ErrorInfo) => {
							if (!isChunkLoadError(error)) {
								void messagePerformanceTracker?.markFailure(error);
							}
							onError && onError(error, info);
						};

					return {
						/** To be called when the message is unmounted  */
						markMessageUnmounted: messagePerformanceTracker?.abort,
						/** To be called when the message fails to render */
						markMessageFailed: markMessageFailed,
						/** To be rendered when the message is successfully loaded */
						messageSuccessComponent: <TrackMount onMount={onMessageSuccess} />,
					};
				}, [messageTemplateId]);

				useEffect(() => {
					return () => {
						void markMessageUnmounted?.();
					};
				}, [markMessageUnmounted]);

				const Core = () => {
					const Component = messageComponents?.[messageTemplateId];

					// This should not happen but things can happen at runtime;
					if (!Component) {
						// Throw to error boundary
						throw new InvalidMessageTemplateIdError(
							JSON.stringify({ messageTemplateId, messageInstanceId }),
						);
					}

					let validatedMessage;
					if (messageValidator) {
						// The messageValidator should also throw an error if the validation failed,
						// which will again be thrown here and should be caught by the ErrorBoundary
						validatedMessage = messageValidator(data);
						if (!validatedMessage) {
							throw new MessageRendererEmptyValidationResponseError();
						}
					}

					const loadingComponent = useMemo(
						() =>
							isUFOEnabled ? (
								<UFOv2.UFOLoadHold name={UFOv2.HoldNames.MESSAGE}>
									{loadingFallback ?? null}
								</UFOv2.UFOLoadHold>
							) : (
								<>{loadingFallback ?? null}</>
							),
						// oxlint-disable-next-line react-hooks/exhaustive-deps
						[loadingFallback, isUFOEnabled],
					);

					// This any should be factored out as part of validation work
					const componentProps: any = messageValidator ? validatedMessage : data;
					// Client-triggered messages: hydrate must return triggerType at top level
					// (e.g. triggerType: messageContext.triggerType). Single source of truth.
					const triggerType = (componentProps as { triggerType?: TriggerType }).triggerType;

					const hasTrigger =
						isTriggerChoreographingEnabled &&
						isClientTriggeredMessage(triggerType as TriggerType | undefined);

					// Create waitForTrigger for triggered messages
					const { waitForTrigger } = useWaitForTrigger({
						hasTrigger,
						messageSuccessComponent,
						messageInstanceId,
						messageTemplateId,
						messageCategory,
					});

					// messageTemplateId exists in the API but component is empty (should not happen)
					if (!Component) {
						return null;
					}

					const messageContextValue = useMemo<MessageContextData>(
						() => ({
							messageInstanceId,
							messageTemplateId,
							triggerId,
							messageCreationType,
							transactionAccountId: componentProps.transactionAccountId,
							analyticsDetails: postOffice?.analyticsDetails,
							journeyId,
							journeyTriggerId,
							configSetId,
						}),
						[messageTemplateId, componentProps.transactionAccountId, journeyTriggerId],
					);

					const messageContent = (
						<FeatureFlaggedUfoWrapper
							messageTemplateId={messageTemplateId}
							isEnabled={isUFOEnabled}
						>
							<MessageContextProvider value={messageContextValue}>
								<RecommendationProvider value={recommendationSession ?? STABLE_EMPTY_OBJECT}>
									<SuspenseBoundary fallback={loadingComponent}>
										<MessageAnalyticsContextProvider>
											<MaybeI18nProvider
												messageTemplateId={messageTemplateId}
												placementId={placementId}
												isI18nMigrationInAppEnabled={isI18nMigrationInAppEnabled}
											>
												<MaybeManualMessageViewedEvent isManual={!!waitForTrigger}>
													{!waitForTrigger && messageSuccessComponent}
													<Component
														onError={onError}
														messageInstanceId={messageInstanceId}
														{...componentProps}
														waitForTrigger={waitForTrigger}
													/>
												</MaybeManualMessageViewedEvent>
											</MaybeI18nProvider>
										</MessageAnalyticsContextProvider>
									</SuspenseBoundary>
								</RecommendationProvider>
							</MessageContextProvider>
						</FeatureFlaggedUfoWrapper>
					);

					// Triggered messages handle choreography with TriggeredMessageValidator
					if (waitForTrigger) {
						return (
							<TriggeredMessageValidator waitForTrigger={waitForTrigger}>
								{messageContent}
							</TriggeredMessageValidator>
						);
					}

					// Regular messages use automatic choreography
					return (
						<ChoreographerWrapper
							messageId={messageInstanceId}
							messageTemplateId={messageTemplateId}
							messageCategory={messageCategory}
						>
							{messageContent}
						</ChoreographerWrapper>
					);
				};

				return withErrorBoundary(Core)({
					errorBoundaryLocation: 'placement-message-renderer-component',
					messageTemplateId,
					fallbackRender,
					onError: markMessageFailed(onError),
				});
			},
		);
	};

	return {
		initializeMessageRenderer,
		MessageRenderer: initializeMessageRenderer(),
	};
};
